import { useMemo } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { TagColor } from '@all-manager/design-system-v2';
import type { DocumentIndexingItem, DocumentIndexingStatus } from '../../types/documentIndexing';


export type Props = {
  item: DocumentIndexingItem;
  divider?: boolean;
  onViewContent: (item: DocumentIndexingItem) => void;
  onViewError: (item: DocumentIndexingItem) => void;
};

export function getDocumentIndexingStatusInfo(status: DocumentIndexingStatus): {
  statusColor: TagColor;
  statusLabel: string;
} {
  switch (status) {
    case 'done':
      return { statusColor: 'done', statusLabel: 'Indexé' };
    case 'pending':
      return { statusColor: 'pending', statusLabel: 'En attente' };
    case 'error':
      return { statusColor: 'cancelled', statusLabel: 'Erreur' };
    case 'unsupported':
      return { statusColor: 'default', statusLabel: 'Non pris en charge' };
    default:
      return { statusColor: 'default', statusLabel: status };
  }
}


export function useDocumentIndexingItem(props: Props) {
  const item = props.item;

  const { statusColor, statusLabel } = useMemo(
    () => getDocumentIndexingStatusInfo(item.status),
    [item.status]
  );


  const { formattedExtractedAt, relativeExtractedAt } = useMemo(() => {
    if (!item.extractedAt) {
      return { formattedExtractedAt: null, relativeExtractedAt: null };
    }
    try {
      const date = new Date(item.extractedAt);
      return {
        formattedExtractedAt: format(date, "d MMMM yyyy 'à' HH:mm", { locale: fr }),
        relativeExtractedAt: formatDistanceToNow(date, { addSuffix: true, locale: fr }),
      };
    } catch {
      return { formattedExtractedAt: null, relativeExtractedAt: null };
    }
  }, [item.extractedAt]);

  const canViewContent = item.status === 'done' || item.hasContent;
  const hasError = item.status === 'error' || item.status === 'unsupported';

  function handleViewContent() {
    props.onViewContent(item);
  }

  function handleViewError() {
    props.onViewError(item);
  }

  function handleOpenFile() {
    if (item.fileUrl) {
      window.open(item.fileUrl, '_blank', 'noopener,noreferrer');
    }
  }

  return {
    item,
    statusColor,
    statusLabel,
    formattedExtractedAt,
    relativeExtractedAt,
    canViewContent,
    hasError,
    handleViewContent,
    handleViewError,
    handleOpenFile,
  };
}
